import React, { FC, useContext, useEffect, useState } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TablePagination,
  TableRow
} from '@material-ui/core';

import { GlobalContext } from '../../../../store';
import { IResult } from '../../../../store/admin/roundResults/store';
import { IGroupEnrollment } from '../../../../store/player/games/store';

import { sortResultsWithTiebreak } from '../../../../contrib/sortResultsWithTiebreak';
import { useAssignResultsState } from '../../../admin/EventWizard/Common/AssignResults/Context/AssignResults.context';
import { useEventLeaderboardsState } from '../Context/EventLeaderboards.context';
import { useRouteInfoState } from '../../../../components/RouteInfo/Context/RouteInfo.context';
import { useGetPlayerEvent } from '../../../../contrib/Hooks/useGetPlayerEvent';
import ScoreTableTableHead from './Components/ScoreTableTableHead';
import ScoreTableExpandedRow from './Components/ScoreTableExpandedRow';
import ScoreTableCollapsedRow from './Components/ScoreTableCollapsedRow';

interface IScoreTable {
  flightId: number;
  roundId?: number;
}

export const ScoreTable: FC<IScoreTable> = props => {
  const { flightId, roundId } = props;

  const {
    actions: {
      roundResults: { get: getRoundResults }
    },
    roundResults
  } = useContext(GlobalContext);

  const { eventId } = useRouteInfoState();

  const event = useGetPlayerEvent(eventId);

  const [collapsedRow, setCollapsedRow] = useState<null | number>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [player, setPlayer] = useState<IGroupEnrollment>();
  const [singleRoundEvent, setSingleRoundEvent] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number>(0);
  const [formatIsStableford, setFormatIsStableford] = useState(false);
  const [sortedResults, setSortedResults] = useState<IResult[]>([]);

  const { results, selectedScoreType, selectedRoundFormat } = useAssignResultsState();
  const { selectedRound } = useEventLeaderboardsState();

  useEffect(() => {
    setCollapsedRow(null);
  }, [roundId, flightId, selectedScoreType]);

  useEffect(() => {
    if (selectedRoundFormat) {
      setFormatIsStableford(selectedRoundFormat.scoringFormat === 1);
    }
  }, [selectedRoundFormat]);

  const handleChangePage = (_EVENT: any, pageNumber: number) => {
    setPage(pageNumber);
    setCollapsedRow(null);
  };

  const handleChangeRowsPerPage = (evnt: any) => {
    setPage(0);
    setRowsPerPage(evnt.target.value);
  };

  useEffect(() => {
    const newSortedResults = sortResultsWithTiebreak(
      results,
      selectedScoreType,
      formatIsStableford
    );

    setSortedResults(newSortedResults);
  }, [results, selectedScoreType, formatIsStableford]);

  const columnNumber =
    roundId && roundId > 0 ? (selectedRound ? [0, 1, 2, 3, 4] : [0, 1, 2, 3]) : [0, 1, 2];

  useEffect(() => {
    if (!roundResults[-1] && eventId) {
      getRoundResults(-1, eventId);
    }
  }, [roundResults]);

  useEffect(() => {
    if (event) {
      setSingleRoundEvent(event.rounds.length === 1);
    }
  }, [event]);

  return (
    <div className="event-leaderboards-table">
      <Table className="leaderboard-score-table">
        <colgroup>
          {columnNumber.map(index => (
            <col key={index} />
          ))}
        </colgroup>

        <ScoreTableTableHead singleRoundEvent={singleRoundEvent} roundId={roundId} />

        <TableBody className="leaderboard__tbody">
          {!!sortedResults && sortedResults.length > 0 ? (
            sortedResults
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((playerResult: IResult, index: number) => [
                <ScoreTableCollapsedRow
                  collapsedRow={collapsedRow}
                  flightId={flightId}
                  formatIsStableford={formatIsStableford}
                  index={index}
                  key={`hiddenTableRow-collapsed-${playerResult.enrollmentId}`}
                  playerResult={playerResult}
                  roundId={roundId}
                  setCollapsedRow={setCollapsedRow}
                  setPlayer={setPlayer}
                  setSelectedGroupId={setSelectedGroupId}
                  singleRoundEvent={singleRoundEvent}
                />,
                <ScoreTableExpandedRow
                  collapsedRow={collapsedRow}
                  formatIsStableford={formatIsStableford}
                  index={index}
                  key={`hiddenTableRow-expanded-${playerResult.enrollmentId}`}
                  player={player}
                  selectedGroupId={selectedGroupId}
                />
              ])
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="no-results">
                No results to show
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        <TableFooter>
          <TablePagination
            rowsPerPageOptions={[200, 250, 300]}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={handleChangePage}
            count={sortedResults.length}
          />
        </TableFooter>
      </Table>
    </div>
  );
};
